import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/api";
import { PageHeader, EmptyState, SlabBadge } from "@/components/Shared";
import { DynamicFields } from "@/components/DynamicFields";
import { FileUploadField } from "@/components/FileUpload";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, BookOpen, Users } from "lucide-react";

const EMPTY = { name: "", type: "b2b", category: "shopkeeper", gstin: "", phone: "", email: "", address: "", state: "", credit_days: 0, contact_person: "", pan: "", aadhaar: "", photo: "", aadhaar_doc: "", pan_doc: "", opening_balance: 0 };

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const openNew = () => { setEditing(null); setForm(EMPTY); setCustom({}); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c.client_id);
    setForm({ name: c.name, type: c.type, category: c.category || "shopkeeper", gstin: c.gstin || "", phone: c.phone || "", email: c.email || "", address: c.address || "", state: c.state || "", credit_days: c.credit_days || 0, contact_person: c.contact_person || "", pan: c.pan || "", aadhaar: c.aadhaar || "", photo: c.photo || "", aadhaar_doc: c.aadhaar_doc || "", pan_doc: c.pan_doc || "", opening_balance: c.opening_balance || 0 });
    setCustom(c.custom || {});
    setOpen(true);
  };

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (form.type === "b2b" && !form.gstin.trim()) return toast.error("GSTIN is mandatory for B2B clients");
    setBusy(true);
    try {
      const payload = { ...form, category: form.type === "b2b" ? form.category : null, custom };
      if (editing) {
        await api.put(`/clients/${editing}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/clients", payload);
        toast.success("Client registered — ledger created");
      }
      setOpen(false); setForm(EMPTY); setCustom({}); setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save client");
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client ${c.name}?`)) return;
    try {
      await api.delete(`/clients/${c.client_id}`);
      toast.success("Client deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients — B2B & B2C" sub="B2B clients need GSTIN + a price category · B2C bills at Retail slab">
        <Button onClick={openNew} data-testid="add-client-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> Register Client
        </Button>
      </PageHeader>

      {clients.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet" sub="Register your first B2B or B2C client" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]" data-testid="clients-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Client</th><th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Price Slab</th><th className="px-5 py-3">GSTIN</th>
                <th className="px-5 py-3">Contact</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {clients.map((c) => (
                <tr key={c.client_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`client-row-${c.client_id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">{c.name}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full border text-[11px] font-mono2 uppercase ${c.type === "b2b" ? "border-blue-500/40 text-blue-600 bg-blue-500/10" : "border-slate-500/40 text-slate-600 bg-slate-500/10"}`}>
                      {c.type}
                    </span>
                  </td>
                  <td className="px-5 py-3.5"><SlabBadge slab={c.type === "b2b" ? c.category : "retail"} /></td>
                  <td className="px-5 py-3.5 font-mono2 text-emerald-600">{c.gstin || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-600">{c.phone || c.email || "—"}</td>
                  <td className="px-5 py-3.5 text-right space-x-1">
                    <button onClick={() => navigate(`/app/ledgers?type=client&id=${c.client_id}`)} data-testid={`client-ledger-${c.client_id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                      <BookOpen className="w-3.5 h-3.5" /> Ledger
                    </button>
                    {user?.role === "admin" && (
                      <>
                        <button onClick={() => openEdit(c)} data-testid={`client-edit-${c.client_id}`}
                          className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-slate-700/50 transition-colors duration-150">
                          Edit
                        </button>
                        <button onClick={() => remove(c)} data-testid={`client-delete-${c.client_id}`}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg" data-testid="add-client-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Client" : "Register Client"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div className="col-span-2 flex gap-2" data-testid="client-type-toggle">
              {["b2b", "b2c"].map((t) => (
                <button type="button" key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`client-type-${t}`}
                  className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 ${
                    form.type === t ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700" : "border-slate-300 text-slate-600 hover:border-slate-600"
                  }`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Client Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            {form.type === "b2b" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">GSTIN * (mandatory for B2B)</Label>
                  <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="27AAAAA0000A1Z5" data-testid="client-gstin-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Price Category *</Label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="client-category-select"
                    className="w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900">
                    <option value="shopkeeper">Shopkeeper</option>
                    <option value="dealer">Dealer</option>
                    <option value="distributor">Distributor</option>
                  </select>
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="client-state-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Credit Terms (days) — invoice due date is auto-computed from this</Label>
              <Input type="number" min="0" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: Number(e.target.value) })} data-testid="client-credit-days-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Opening Balance / Old Dues (₹) — they owe us</Label>
              <Input type="number" step="0.01" min="0" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })} data-testid="client-opening-balance-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Contact Person</Label>
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="client-contact-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">PAN No</Label>
              <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} data-testid="client-pan-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Aadhaar No</Label>
              <Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} data-testid="client-aadhaar-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <FileUploadField label="Current Photo" docType="photo" image value={form.photo}
              onChange={(p) => setForm({ ...form, photo: p })} accept="image/*" testid="client-photo-upload" />
            <FileUploadField label="Aadhaar Doc" docType="aadhaar_doc" value={form.aadhaar_doc}
              onChange={(p) => setForm({ ...form, aadhaar_doc: p })} testid="client-aadhaar-doc-upload" />
            <FileUploadField label="PAN Doc" docType="pan_doc" value={form.pan_doc}
              onChange={(p) => setForm({ ...form, pan_doc: p })} testid="client-pan-doc-upload" />
            <DynamicFields entity="clients" values={custom} onChange={(k, v) => setCustom({ ...custom, [k]: v })} />
            <div className="col-span-2 pt-2">
              <Button type="submit" disabled={busy} data-testid="client-save-btn"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
                {busy ? "Saving…" : "Register Client"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
